import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useIsAuthenticated, useMsal, useAccount } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { get } from '../../config/authConfig';
import { Role, Permission } from '../../types/auth';
import api from '../../api/axios';

interface User {
  name?: string;
  email: string;
  tenantId: string;
  homeAccountId: string;
  roles: string[];
  primaryRole: string | null;
  permissions: string[];
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: () => void;
  logout: () => void;
  getAccessToken: () => Promise<string | null>;
  loading: boolean;
  hasPermission: (permission: Permission | string) => boolean;
  hasAnyPermission: (...permissions: (Permission | string)[]) => boolean;
  hasRole: (role: Role | string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = useAccount(accounts[0] || {});
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Gérer les callbacks de redirection
  useEffect(() => {
    instance.handleRedirectPromise()
      .then((_response) => {
        // handled
      })
      .catch((error) => {
        console.error('AuthProvider: Erreur lors du traitement du redirect:', error);
      });
  }, [instance]);

  const fetchUserRoles = useCallback(async (accessToken: string) => {
    try {
      // Passe par l'instance axios (baseURL + intercepteurs token/erreur) au lieu d'un fetch() brut.
      // On garde le token explicite : l'intercepteur le ré-acquiert et l'écrase si dispo, sinon ce
      // header de secours subsiste. Axios lève sur non-2xx → géré par le catch (perms vides, comme avant).
      const { data } = await api.get('/auth/me', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      return {
        roles: data.roles || [],
        primaryRole: data.primary_role || null,
        permissions: data.permissions || [],
      };
    } catch (error) {
      console.error('Failed to fetch user roles:', error);
    }
    return { roles: [], primaryRole: null, permissions: [] };
  }, []);

  useEffect(() => {
    if (isAuthenticated && account) {
      const initUser = async () => {
        let roles: string[] = [];
        let primaryRole: string | null = null;
        let permissions: string[] = [];

        try {
          const tokenResponse = await instance.acquireTokenSilent({
            ...get.apiTokenRequest(),
            account: account,
          });
          const token = get.apiScope()
            ? tokenResponse.accessToken
            : tokenResponse.idToken;
          const rbacData = await fetchUserRoles(token);
          roles = rbacData.roles;
          primaryRole = rbacData.primaryRole;
          permissions = rbacData.permissions;
        } catch (error) {
          console.error('Failed to acquire token for roles:', error);
        }

        setUser({
          name: account.name,
          email: account.username,
          tenantId: account.tenantId,
          homeAccountId: account.homeAccountId,
          roles,
          primaryRole,
          permissions,
        });
        setLoading(false);
      };

      initUser();
    } else if (inProgress === InteractionStatus.None) {
      setUser(null);
      setLoading(false);
    }
  }, [isAuthenticated, account, instance, fetchUserRoles, inProgress]);

  const login = async () => {
    try {
      await instance.loginPopup(get.loginRequest());
    } catch (error) {
      console.error('AuthProvider: Erreur lors de la connexion popup:', error);
      try {
        await instance.loginRedirect(get.loginRequest());
      } catch (redirectError) {
        console.error('AuthProvider: Erreur redirect aussi:', redirectError);
      }
    }
  };

  const logout = () => {
    instance.logoutPopup({
      postLogoutRedirectUri: window.location.origin,
      mainWindowRedirectUri: window.location.origin,
    });
  };

  const getAccessToken = async (): Promise<string | null> => {
    if (!account) return null;

    try {
      const response = await instance.acquireTokenSilent({
        ...get.apiTokenRequest(),
        account: account,
      });
      return get.apiScope() ? response.accessToken : response.idToken;
    } catch (error) {
      console.error('Erreur lors de la récupération du token:', error);
      try {
        const response = await instance.acquireTokenPopup({
          ...get.apiTokenRequest(),
          account: account,
        });
        return get.apiScope() ? response.accessToken : response.idToken;
      } catch (popupError) {
        console.error('Erreur popup token:', popupError);
        return null;
      }
    }
  };

  // Vérification que l'utilisateur appartient au bon tenant
  useEffect(() => {
    if (isAuthenticated && account) {
      // @ts-ignore - idTokenClaims exists on AccountInfo but types might be tricky
      const tid = account.idTokenClaims?.tid;
      if (tid && tid !== get.expectedTenantId()) {
        console.error('Tenant mismatch: Vous devez utiliser un compte de votre organisation.');
        instance.logoutRedirect();
        return;
      }
    }
  }, [isAuthenticated, account, instance]);

  const permissionSet = React.useMemo(
    () => new Set(user?.permissions ?? []),
    [user?.permissions]
  );
  const rbacActive = (user?.roles?.length ?? 0) > 0;

  const hasPermission = useCallback(
    (permission: Permission | string): boolean => {
      if (!rbacActive) return true;
      return permissionSet.has(permission);
    },
    [rbacActive, permissionSet]
  );

  const hasAnyPermission = useCallback(
    (...permissions: (Permission | string)[]): boolean => {
      if (!rbacActive) return true;
      return permissions.some((p) => permissionSet.has(p));
    },
    [rbacActive, permissionSet]
  );

  const hasRole = useCallback(
    (role: Role | string): boolean => {
      if (!rbacActive) return true;
      return user?.roles?.includes(role) ?? false;
    },
    [rbacActive, user?.roles]
  );

  const value: AuthContextType = {
    isAuthenticated,
    user,
    login,
    logout,
    getAccessToken,
    loading: loading || inProgress === InteractionStatus.Startup,
    hasPermission,
    hasAnyPermission,
    hasRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth doit être utilisé dans un AuthProvider');
  }
  return context;
};
